<template>
  <div class="flex gap-2 mb-5 w-full">
    <div v-if="!onlyTone" class="flex flex-col items-center text-xs w-[90px]">
      <div class="relative">
        <UserAvatar
          :avatar-url="originCharacter?.avatarMedia?.url"
          class="w-[90px]"
        />
        <i
          v-if="store.form.characterzz.length > 1 && idx !== -1"
          class="iconfont icon-xmark absolute top-[-10px] right-[-10px] rounded-full bg-primary text-white text-xs p-[2px] w-5 text-center"
          @click="clickRemoveCharacter(idx)"
        />
      </div>
      <p class="text-white my-1">
        {{ originCharacter?.name }}
      </p>
      <VanPopover
        v-if="originCharacter?.role !== 3"
        v-model:show="character.isOpen"
        :actions="actions"
        @select="onSelectCharacterRole"
      >
        <template #reference>
          <p @click="store.activeCharacterId = character.id!">
            {{ t(`game.role.${originCharacter.role}`) }}
            <i class="iconfont icon-down1 text-xs"></i>
          </p>
        </template>
      </VanPopover>

      <p v-else>
        {{ t(`game.role.${originCharacter.role}`) }}
      </p>

      <button
        v-if="originCharacter.role === 3"
        class="btn btn-xs rounded mt-1"
        @click="(store.uploadType = 0), (store.showPlayerEditModal = true)"
      >
        {{ $t("Buttons.edit") }}
      </button>
    </div>
    <div class="flex-1">
      <template v-if="!onlyTone">
        <h2 class="text-base font-bold mb-2">
          {{ $t("game.base.introduction") }}
        </h2>
        <div class="bg-[--van-cell-background]">
          <VanField
            v-model="character.introduction"
            :autosize="{ minHeight: 100 }"
            :border="false"
            rows="1"
            type="textarea"
            @blur="store.saveBase"
          />

          <div class="flex items-center justify-end pr-4 pb-3">
            <button
              class="btn btn-primary btn-xs rounded-sm mr-1"
              @click="
                store.clickAutoGenerate(
                  idx === -1 ? 'player' : 'characterzz',
                  character.id
                )
              "
            >
              {{ $t(`story.auto`) }}
            </button>
            <button
              class="btn btn-primary btn-xs rounded-sm"
              @click="clickClearContent"
            >
              <i class="iconfont icon-delete text-xs" />
            </button>
          </div>
        </div>
      </template>

      <div class="flex justify-between items-center">
        <h2 class="text-base font-bold my-2.5">
          {{ $t("game.show.defaultTone") }}
        </h2>

        <div class="flex items-center">
          <button
            class="btn btn-primary btn-xs rounded-sm mr-1"
            @click="clickViewDialog"
          >
            {{ $t("game.viewConversation") }}
          </button>
        </div>
      </div>

      <div class="bg-[--van-cell-background] p-2.5">
        <div class="flex gap-2 items-center">
          <div :class="[character.mateToneId ? '' : 'text-[#E84B4B]']">
            {{
              character.mateToneId
                ? toneStore.toneMap[character.mateToneId!]?.name
                : $t("game.show.unselected")
            }}
          </div>
          <button
            class="btn btn-primary btn-xs rounded-sm mr-1"
            @click="store.clickSelectCharacterTone(character.id!)"
          >
            {{
              character.mateToneId
                ? $t("game.show.reselect")
                : $t("game.show.selectTone")
            }}
          </button>
          <button
            v-if="character.mateToneId"
            class="btn btn-primary btn-xs rounded-sm"
            @click="clickRemoveTone"
          >
            <i class="iconfont icon-delete text-xs" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useGalGameEditor } from "@/stores/galGameEditor";
import { useStoryBook } from "@/stores/story-book";
import { useTone } from "@/stores/tone";
import { StorageGet, StorageRemove } from "@/tools/localStorage";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { computed } from "vue";
import { PropType } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const store = useGalGameEditor();
const storyBookStore = useStoryBook();
const toneStore = useTone();

const props = defineProps({
  character: {
    type: Object as PropType<GalGameCharacter>,
    required: true,
  },

  onlyTone: {
    type: Boolean,
    default: false,
  },

  idx: {
    type: Number,
    required: true,
  },
});

const originCharacter = computed(() => {
  return store.characterIdMap[props.character.id || -1];
});

const actions = [
  { text: t("game.role.1"), role: 1 },
  { text: t("game.role.2"), role: 2 },
];

const onSelectCharacterRole = async (action: any) => {
  const { role } = action;

  if (role === 1) {
    // 只能有一个主角
    const protagonistIdx = store.form.characterzz.findIndex(
      (v) => v.role === 1
    );
    if (protagonistIdx !== -1) {
      // 已经有主角了，提示是否切换
      try {
        await showConfirmDialog({
          title: t("ConfirmModal.protagonistExists"),
          message: t("ConfirmModal.protagonistExistsText"),
          confirmButtonText: t("Buttons.confirm"),
          cancelButtonText: t("Buttons.cancel"),
        });
        const protagonist = store.form.characterzz[protagonistIdx];
        protagonist.role = 2;
        store.characterIdMap[protagonist.id!].role = 2;
      } catch (e) {
        // 取消
        return;
      }
    }
  }

  const idx = store.form.characterzz.findIndex(
    (v) => v.id === store.activeCharacterId
  );
  store.characterIdMap[store.activeCharacterId!].role = role;
  if (idx !== -1) {
    store.form.characterzz[idx].role = role;
    store.saveBase();
  }
};

const clickRemoveCharacter = (idx: number) => {
  showConfirmDialog({
    title: t("ConfirmModal.deleteCharacter"),
    message: t("ConfirmModal.deleteCharacterText"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    const loading = showLoadingToast({
      message: t("Message.removingRole"),
    });
    const [{ id, mateId, role }] = store.form.characterzz.splice(idx, 1);

    await store.saveBase();
    await store.resetNode();

    const characterIdCache = StorageGet(
      `galGameRoleSaveCache-${store.galGameId}-${role}`
    );
    if (characterIdCache === id)
      StorageRemove(`galGameRoleSaveCache-${store.galGameId}-${role}`);

    delete store.characterIdMap[id!];

    loading.close();

    if (!storyBookStore.form.characterzz) storyBookStore.form.characterzz = [];
    storyBookStore.form.characterzz = storyBookStore.form.characterzz.filter(
      (v) => v.mateId !== mateId
    );
  });
};

const clickClearContent = () => {
  showConfirmDialog({
    title: t("ConfirmModal.clearContent"),
    message: t("ConfirmModal.clearContentText"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(() => {
    props.character.introduction = "";
    store.saveBase();
  });
};

const clickRemoveTone = () => {
  store.activeCharacterId = props.character.id!;
  showConfirmDialog({
    title: t("ConfirmModal.deleteTone"),
    message: t("ConfirmModal.deleteToneText"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(() => {
    store.onUpdateCharacterTone();
  });
};

const clickViewDialog = () => {
  store.activeCharacterId = props.character.id!;
  store.activeStoryNodeId = undefined;
  store.characterDialogList = [];
  store.showCharacterAllDialogModal = true;
};
</script>
